import { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useInventoryAppServices } from '@/app/services/InventoryAppServicesContext';
import { useStartupSession } from '@/app/session/StartupSessionContext';
import type { InventorizationContext } from '@/features/inventorization/domain/inventorization-context';
import { updateInventorizationContext } from '@/features/inventorization/domain/inventorization-context';
import type { InventorizationIdentity } from '@/features/inventorization/domain/inventorization-identity';
import { updateInventorizationIdentity } from '@/features/inventorization/domain/inventorization-identity';
import { applyMatchedMeterToInventorizationSession } from '@/features/inventorization/matched-meter-session';
import { createCaptureSession, WORKFLOW_TYPES } from '@/shared/capture/capture-session';
import type { MeterSearchResult } from '@/shared/meter-identification';

interface DraftState {
  isCreating: boolean;
  latestDraftId: string | null;
  error: string | null;
}

export interface CreateInventorizationDraftInput {
  context?: Partial<InventorizationContext>;
  identity?: Partial<InventorizationIdentity>;
  matchedMeter?: MeterSearchResult;
  startWizard?: boolean;
}

export function useCreateInventorizationDraft() {
  const { activeLocation, snapshot } = useStartupSession();
  const repositories = useInventoryAppServices();
  const [state, setState] = useState<DraftState>({
    isCreating: false,
    latestDraftId: null,
    error: null,
  });

  async function createDraft(input: CreateInventorizationDraftInput = {}) {
    setState(current => ({ ...current, isCreating: true, error: null }));

    try {
      const createdAt = new Date().toISOString();
      const draft = createCaptureSession({
        id: `session-${uuidv4()}`,
        workflowType: WORKFLOW_TYPES.inventorize,
        locationId: activeLocation?.locationId,
        sourceTime: createdAt,
      });
      const withSessionContext = {
        ...draft,
        createdBy: snapshot.email || draft.createdBy,
        locationApiKey: activeLocation?.apiKey ?? draft.locationApiKey,
      };
      const withMatchedMeter = input.matchedMeter
        ? applyMatchedMeterToInventorizationSession(withSessionContext, input.matchedMeter, createdAt)
        : withSessionContext;
      const withInputContext = input.context
        ? updateInventorizationContext(withMatchedMeter, input.context, createdAt)
        : withMatchedMeter;
      const session = input.identity
        ? updateInventorizationIdentity(withInputContext, input.identity, createdAt)
        : withInputContext;
      const savedSession = input.startWizard
        ? {
            ...session,
            data: {
              ...session.data,
              inventorization: {
                ...(session.data.inventorization as object),
                startWizard: true,
              },
            },
          }
        : session;

      await repositories.captureSessions.save(savedSession);

      setState({
        isCreating: false,
        latestDraftId: savedSession.id,
        error: null,
      });

      return savedSession;
    } catch (error) {
      setState({
        isCreating: false,
        latestDraftId: null,
        error: error instanceof Error ? error.message : 'Unable to create draft.',
      });

      return undefined;
    }
  }

  return {
    ...state,
    createDraft,
  };
}
